import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Flag, BadgeCheck, VenetianMask } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CAT_COLORS = {
  confession: "bg-[#6C5CE7]/15 text-[#6C5CE7]", meme: "bg-[#F9C31F] text-[#12211C]",
  campus_tea: "bg-primary/10 text-primary", question: "bg-secondary/20 text-[#14532D]",
  discussion: "bg-black/5 text-foreground", poll: "bg-secondary/20 text-[#14532D]", event: "bg-primary/10 text-primary",
};

const REPORT_CATS = ["harassment", "bullying", "spam", "personal_information", "threats", "hate", "sexual_content", "false_accusations", "other"];

export const PostCard = ({ post, onChange }) => {
  const [liked, setLiked] = useState(post.liked);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [reportCat, setReportCat] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/react`);
      setLiked(data.liked);
      setLikeCount((c) => c + (data.liked ? 1 : -1));
    } catch (e) { toast.error(apiError(e)); }
  };

  const vote = async (i) => {
    try {
      await api.post(`/posts/${post.id}/vote`, { option_index: i });
      onChange?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitReport = async () => {
    if (!reportCat) return toast.error("Select a category");
    try {
      await api.post("/reports", { target_type: "post", target_id: post.id, category: reportCat, details: reportDetails });
      toast.success("Report submitted. Thanks for keeping campus safe.");
      setReportOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const totalVotes = post.poll_options?.reduce((s, o) => s + o.votes, 0) || 0;

  return (
    <div className="t-card p-5" data-testid={`post-card-${post.id}`}>
      <div className="flex items-center gap-3">
        {post.author?.anonymous ? (
          <div className="w-9 h-9 rounded-full bg-[#6C5CE7]/15 flex items-center justify-center">
            <VenetianMask className="w-4 h-4 text-[#6C5CE7]" />
          </div>
        ) : (
          <div className="w-9 h-9 rounded-full bg-secondary/30 flex items-center justify-center font-bold text-sm">
            {post.author?.name?.[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-sm truncate">
              {post.author?.anonymous ? "Anonymous" : post.author?.name}
            </span>
            {post.author?.anonymous ? (
              <span className="text-[10px] font-bold text-[#6C5CE7] uppercase tracking-wider">Verified Member</span>
            ) : post.author?.verified ? (
              <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
            ) : null}
          </div>
          <span className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</span>
        </div>
        <span className={`t-chip ${CAT_COLORS[post.category] || "bg-black/5"}`}>{post.category.replace("_", " ")}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
      {post.image_base64 && (
        <img src={`data:image/jpeg;base64,${post.image_base64}`} alt="" className="mt-3 rounded-2xl max-h-80 w-full object-cover" />
      )}
      {post.gif_url && (
        <img src={post.gif_url} alt="GIF" className="mt-3 rounded-2xl max-h-72" data-testid={`post-gif-${post.id}`} />
      )}
      {post.video_url && (() => {
        const yt = post.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
        return yt ? (
          <iframe title="video" src={`https://www.youtube.com/embed/${yt[1]}`} allowFullScreen
            className="mt-3 rounded-2xl w-full aspect-video border-0" data-testid={`post-video-${post.id}`} />
        ) : (
          <video src={post.video_url} controls className="mt-3 rounded-2xl w-full max-h-80" data-testid={`post-video-${post.id}`} />
        );
      })()}
      {post.poll_options && (
        <div className="mt-3 space-y-2">
          {post.poll_options.map((o, i) => {
            const pct = totalVotes ? Math.round((o.votes / totalVotes) * 100) : 0;
            return (
              <button key={`${o.text}-${i}`} data-testid={`poll-option-${i}`} onClick={() => vote(i)}
                className={`relative w-full text-left rounded-2xl border px-4 py-2.5 text-sm overflow-hidden transition-colors duration-200 ${o.voted ? "border-primary" : "border-black/10 hover:border-black/25"}`}>
                <div className="absolute inset-y-0 left-0 bg-secondary/25 rounded-2xl" style={{ width: `${pct}%` }} />
                <span className="relative font-semibold">{o.text}</span>
                <span className="relative float-right text-xs text-muted-foreground">{pct}%</span>
              </button>
            );
          })}
          <p className="text-xs text-muted-foreground">{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</p>
        </div>
      )}
      <div className="flex items-center gap-4 mt-4">
        <button data-testid={`like-btn-${post.id}`} onClick={toggleLike}
          className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 ${liked ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
          <Heart className={`w-4 h-4 ${liked ? "fill-primary" : ""}`} /> {likeCount}
        </button>
        <Link to={`/campus/post/${post.id}`} data-testid={`comments-link-${post.id}`}
          className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-200">
          <MessageCircle className="w-4 h-4" /> {post.comment_count || 0}
        </Link>
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogTrigger asChild>
            <button data-testid={`report-btn-${post.id}`} className="ml-auto text-muted-foreground hover:text-destructive transition-colors duration-200">
              <Flag className="w-4 h-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle>Report post</DialogTitle></DialogHeader>
            <Select value={reportCat} onValueChange={setReportCat}>
              <SelectTrigger data-testid="report-category-select"><SelectValue placeholder="Reason" /></SelectTrigger>
              <SelectContent>
                {REPORT_CATS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Details (optional)" value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} />
            <Button data-testid="report-submit-btn" onClick={submitReport} className="rounded-full">Submit report</Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
